import React, { useState, useRef } from 'react';
import { Eye, Edit3, Bold, Italic, Heading1, Heading2, List, Sigma, Code as CodeIcon, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import TheoryRenderer from './TheoryRenderer';

// TheoryEditor
// ────────────
// Дизайн:
//  • Markdown в textarea — paste-safe by design (любая копия из Word/Google Docs
//    превращается в чистый текст и не ломает разметку).
//  • Тулбар вставляет токены markdown в позицию курсора.
//  • Превью-режим переключается кнопкой; используем тот же TheoryRenderer,
//    что и студент видит — WYSIWYG-консистентность.
//  • onPaste принудительно превращает любую вставку в plainText:
//      e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain'))
//    Это фиксирует требование «не должно ломаться при копировании».

const TheoryEditor = ({ value, onChange, placeholder, minRows = 10 }) => {
  const [mode, setMode] = useState('edit'); // 'edit' | 'preview'
  const ref = useRef(null);

  const insertAround = (before, after = '') => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const selected = (value || '').slice(start, end);
    const next =
      (value || '').slice(0, start) + before + selected + after + (value || '').slice(end);
    onChange(next);
    // Возвращаем фокус и ставим курсор после вставки
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + before.length + selected.length + after.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const insertLinePrefix = (prefix) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const text = value || '';
    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    const next = text.slice(0, lineStart) + prefix + text.slice(lineStart);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + prefix.length, start + prefix.length);
    });
  };

  const handlePaste = (e) => {
    // Принудительно plain-text. Это и есть гарантия «вставка не ломает».
    const text = e.clipboardData?.getData('text/plain');
    if (text == null) return;
    e.preventDefault();
    const el = ref.current;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const next = (value || '').slice(0, start) + text + (value || '').slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-100 dark:border-gray-800 p-2">
        <Btn title="Заголовок 1" onClick={() => insertLinePrefix('# ')}>
          <Heading1 className="w-4 h-4" />
        </Btn>
        <Btn title="Заголовок 2" onClick={() => insertLinePrefix('## ')}>
          <Heading2 className="w-4 h-4" />
        </Btn>
        <Sep />
        <Btn title="Жирный" onClick={() => insertAround('**', '**')}>
          <Bold className="w-4 h-4" />
        </Btn>
        <Btn title="Курсив" onClick={() => insertAround('_', '_')}>
          <Italic className="w-4 h-4" />
        </Btn>
        <Btn title="Список" onClick={() => insertLinePrefix('- ')}>
          <List className="w-4 h-4" />
        </Btn>
        <Btn title="Код" onClick={() => insertAround('`', '`')}>
          <CodeIcon className="w-4 h-4" />
        </Btn>
        <Sep />
        <Btn title="Формула в строке: $x^2$" onClick={() => insertAround('$', '$')}>
          <Sigma className="w-4 h-4" />
          <span className="text-[11px]">inline</span>
        </Btn>
        <Btn title="Формула блоком: $$...$$" onClick={() => insertAround('\n$$\n', '\n$$\n')}>
          <Sigma className="w-4 h-4" />
          <span className="text-[11px]">block</span>
        </Btn>

        <Link
          to="/help/formatting"
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
          title="Справка по форматированию (Markdown + формулы)"
        >
          <HelpCircle className="w-3.5 h-3.5" />
          Справка
        </Link>

        <div className="ml-auto flex rounded-lg bg-gray-100 dark:bg-gray-800 p-0.5">
          <button
            type="button"
            onClick={() => setMode('edit')}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition ${
              mode === 'edit'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500'
            }`}
          >
            <Edit3 className="w-3.5 h-3.5" /> Редактор
          </button>
          <button
            type="button"
            onClick={() => setMode('preview')}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition ${
              mode === 'preview'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500'
            }`}
          >
            <Eye className="w-3.5 h-3.5" /> Превью
          </button>
        </div>
      </div>

      {mode === 'edit' ? (
        <textarea
          ref={ref}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onPaste={handlePaste}
          rows={minRows}
          placeholder={
            placeholder ||
            'Пишите Markdown:\n\n# Заголовок\n## Подзаголовок\n**жирный**, _курсив_, `код`\n\nИнлайн-формула: $a^2 + b^2 = c^2$\n\nБлок-формула:\n$$\n\\int_0^1 x^2 \\, dx = \\tfrac{1}{3}\n$$'
          }
          className="w-full font-mono text-sm leading-6 px-4 py-3 bg-white dark:bg-gray-900 text-gray-900 dark:text-white outline-none resize-y"
        />
      ) : (
        <div className="px-4 py-3 min-h-[240px] max-h-[60vh] overflow-y-auto">
          {value?.trim() ? (
            <TheoryRenderer content={value} />
          ) : (
            <p className="text-sm text-gray-400">Превью появится, когда будет текст.</p>
          )}
        </div>
      )}
    </div>
  );
};

const Btn = ({ children, onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
  >
    {children}
  </button>
);
const Sep = () => <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />;

export default TheoryEditor;
